import { useQuery } from '@shared/modules/use-query/hooks'
import { AppointmentDetails } from '../types'
import { useCallback, useMemo } from 'react'
import { AppointmentsService } from '../services'
import { UseQueryOptions } from '@shared/modules/use-query/types'
import { useBackgroundNavigate } from '@shared/hooks'
import { localURLMaker } from '@shared/utils/url-maker'
import { message, Modal } from 'antd'
import { printAppointmentDocumentUseCase } from '../use-cases/print-document'
import { AppointmentRoutes } from '../constants'

export const useAppointmentDetails = (id: number | string) => {
  const navigate = useBackgroundNavigate()

  const { reFetch, data, loading } = useQuery<AppointmentDetails | null>(
    useCallback(async () => {
      const result = await AppointmentsService.getOneById(id || 0)

      if (result.status === 200) {
        return result.data
      } else {
        throw new Error(result.data)
      }
    }, [id]),
    useMemo<UseQueryOptions>(
      () => ({
        initialData: null,
        enabled: !!id,
      }),
      [id],
    ),
  )

  const edit = useCallback(() => {
    navigate(localURLMaker(AppointmentRoutes.UPDATE, { id: id, userId: data?.customer.id || 0 }))
  }, [data, navigate, id])

  const remove = useCallback(() => {
    const handler = async () => {
      message.loading({ key: 'message', content: 'Əməliyyat aparılır.' })
      const result = await AppointmentsService.actionByIds(id ? [id] : [], 'remove')

      if (result.status === 200) {
        message.success({ key: 'message', content: 'Silmə əməliyyatı müvəffəqiyyətlə başa çatdı' })
        await reFetch()
      } else {
        message.error({ key: 'message', content: result.data })
      }
    }

    Modal.confirm({
      title: 'Silmə əməliyyatını etməyinizə əminsinizmi?',
      content: 'Silmə əməliyyatını etdikdən sonra məlumatların geri qaytarılması mümkün olmayacaq.',
      onOk: handler,
    })
  }, [id, reFetch])

  const updateStatus = useCallback(
    async (stateId: number | string) => {
      const handler = async () => {
        message.loading({ key: 'message', content: 'Əməliyyat aparılır.' })
        const result = await AppointmentsService.updateStatusByIds([id], stateId)

        if (result.status === 200) {
          message.success({ key: 'message', content: 'Əməliyyat müvəffəqiyyətlə başa çatdı' })
          await reFetch()
        } else {
          message.error({ key: 'message', content: result.data as string })
        }
      }

      Modal.confirm({
        title: 'Status dəyişməyə əminsinizmi?',
        content: 'Status dəyişdikdən sonra məlumatların geri qaytarılması mümkün olmayacaq.',
        onOk: handler,
      })
    },
    [id, reFetch],
  )

  const printCheck = useCallback(async () => {
    const action = async () => {
      const result = await AppointmentsService.print(id)

      if (result.status === 200) {
        message.success('Çap edildi.')
      } else {
        message.error(result.data)
      }
    }

    Modal.confirm({
      title: 'Çap et',
      content: 'Qəbulu çap etməyə əminsinizmi?',
      onOk: action,
    })
  }, [id])

  const printDocument = useCallback(() => {
    return printAppointmentDocumentUseCase(id)
  }, [id])

  return { data, loading, reFetch, edit, remove, updateStatus, print: { check: printCheck, document: printDocument } }
}
